import React from "react";
import { motion } from "framer-motion";
import { Award, HeartHandshake, ShieldCheck, Users } from "lucide-react";

const stats = [
  { label: "Begleitete Rückgaben", value: "200+" },
  { label: "Jahre Branchen­erfahrung", value: "15+" },
  { label: "Ø gesparte Nachzahlung", value: "850 €" },
  { label: "Weiterempfehlung", value: "97%" },
];

const values = [
  { icon: ShieldCheck, title: "Unabhängig", desc: "Keine Provisionen vom Händler. Wir arbeiten ausschließlich für Sie." },
  { icon: HeartHandshake, title: "Persönlich", desc: "Sie haben einen festen Ansprechpartner – kein Callcenter." },
  { icon: Award, title: "Erfahren", desc: "Über 15 Jahre in der Automobil­branche – wir kennen jeden Trick." },
  { icon: Users, title: "Auf Augenhöhe", desc: "Ohne Fachchinesisch. Wir erklären, bis Sie sich sicher fühlen." },
];

const About = () => {
  return (
    <section id="ueber-uns" data-testid="about-section" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* Image side */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10">
            <img
              src="https://images.unsplash.com/photo-1583121274602-3e2820c69888?auto=format&fit=crop&w=1200&q=80"
              alt="BMW Fahrzeug-Detail – AutoCoach Aufbereitung"
              className="w-full h-[460px] lg:h-[600px] object-cover"
            />
          </div>
          {/* Decorative card */}
          <div className="absolute -top-6 -right-6 bg-[#1FA3B5] text-white rounded-2xl p-5 shadow-xl shadow-[#1FA3B5]/30 hidden md:block">
            <p className="text-3xl font-heading font-bold leading-none">15+</p>
            <p className="text-xs mt-1 opacity-90">Jahre Erfahrung</p>
          </div>
        </motion.div>

        {/* Text side */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#1FA3B5] mb-4">Über AutoCoach</p>
          <h2 className="font-heading text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.1]">
            Ihr Partner für die<br />
            <span className="text-[#1FA3B5]">entspannte Leasingrückgabe.</span>
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Wer ein Auto leasen, verkaufen oder zurückgeben will, sitzt fast immer dem Profi gegenüber. AutoCoach24
            dreht das um: Wir bringen die gleiche Erfahrung mit – nur eben in Ihrem Auftrag. Klar, ehrlich und ohne
            versteckte Eigeninteressen.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {values.map((v) => (
              <div key={v.title} className="flex gap-4" data-testid={`about-value-${v.title.toLowerCase()}`}>
                <div className="w-11 h-11 rounded-xl bg-[#E6F6F8] text-[#1FA3B5] flex items-center justify-center flex-shrink-0">
                  <v.icon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-slate-900">{v.title}</h4>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Stats strip */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-20 lg:mt-28">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 rounded-3xl overflow-hidden border border-slate-200">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-white px-6 py-10 text-center"
              data-testid={`stat-${i}`}
            >
              <p className="font-heading text-4xl lg:text-5xl font-bold text-slate-900">{s.value}</p>
              <p className="text-sm text-slate-500 mt-2">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
